import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Loader2, Search, CheckCircle, AlertCircle, Sparkles, RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import AlterarSenha from '../../components/AlterarSenha';
import api from '../../utils/api';
import { MUNICIPIOS } from '../../utils/cidades';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001';

// ─── Formata CNPJ para consulta (só números) ──────────────────
function apenasNumeros(v) { return v.replace(/\D/g, ''); }

// ─── Mapeia porte da Receita → porte interno ─────────────────
function mapearPorte(descricao = '') {
  const d = descricao.toUpperCase();
  if (d.includes('MEI'))        return 'mei';
  if (d.includes('MICRO'))      return 'micro';
  if (d.includes('PEQUENA'))    return 'pequena';
  if (d.includes('MÉDIA') || d.includes('MEDIA')) return 'media';
  if (d.includes('GRANDE'))     return 'grande';
  return '';
}

// ─── Mapeia município da Receita → lista interna ──────────────
function mapearCidade(municipio = '') {
  const m = municipio.trim().toUpperCase();
  const match = MUNICIPIOS.find(c => c.toUpperCase() === m);
  return match || '';
}

// ── Componente de descrição com IA ───────────────────────────
function DescricaoIA({ register, watch, setValue }) {
  const [gerando, setGerando] = useState(false);
  const descricao = watch('descricao', '');
  const nomeEmpresa = watch('razao_social') || watch('nome_fantasia') || '';
  const setor = watch('setor') || '';
  const cidade = watch('cidade') || '';
  const cnpj = watch('cnpj') || '';

  const gerarComIA = async () => {
    if (!nomeEmpresa) { toast.error('Preencha o nome ou razão social primeiro.'); return; }
    setGerando(true);
    try {
      const { data } = await api.post('/empresas/ia-descricao', {
        nome: nomeEmpresa, setor, cidade, cnpj: cnpj.replace(/\D/g,''),
      });
      setValue('descricao', data.descricao);
      toast.success('Descrição gerada! Você pode editar antes de salvar.');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao gerar descrição.');
    }
    setGerando(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">Sobre a empresa</label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{(descricao||'').length}/800</span>
          <button type="button" onClick={gerarComIA} disabled={gerando}
            className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 hover:bg-purple-100 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50">
            {gerando
              ? <><Loader2 size={11} className="animate-spin"/> Pesquisando na internet...</>
              : <><Sparkles size={11}/> Gerar com IA</>}
          </button>
        </div>
      </div>
      <textarea
        className="input resize-none"
        rows={6}
        maxLength={800}
        placeholder="Clique em 'Gerar com IA' para a Mikhal pesquisar sua empresa na internet e criar uma descrição automaticamente. Você pode editar depois.&#10;&#10;Ou escreva manualmente: missão, valores, história, diferenciais..."
        {...register('descricao', { maxLength: 800 })}
      />
      {gerando && (
        <div className="mt-2 bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin text-purple-600"/>
          <p className="text-xs text-purple-700">
            A Mikhal está pesquisando dados da sua empresa na internet e gerando uma descrição...
          </p>
        </div>
      )}
      <div className="bg-acre-light/50 border border-acre-green/20 rounded-xl p-3 mt-2">
        <p className="text-xs text-gray-500 font-semibold mb-1">💡 Dicas para uma boa descrição:</p>
        <ul className="text-xs text-gray-400 space-y-0.5 list-disc list-inside">
          <li>Explique o que sua empresa faz e há quanto tempo atua</li>
          <li>Descreva a cultura organizacional e os valores</li>
          <li>Mencione benefícios e oportunidades de crescimento</li>
        </ul>
      </div>
    </div>
  );
}

export default function PerfilEmpresa() {
  const [saving,        setSaving]        = useState(false);
  const [logo,          setLogo]          = useState(null);
  const [logoPreview,   setLogoPreview]   = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [stats,         setStats]         = useState(null);
  const [buscandoCNPJ,  setBuscandoCNPJ]  = useState(false);
  const [cnpjStatus,    setCnpjStatus]    = useState(null); // 'ok' | 'erro' | null

  const { register, handleSubmit, reset, watch, setValue } = useForm();
  const cnpjValor = watch('cnpj', '');

  useEffect(() => {
    api.get('/empresas/perfil').then(r => {
      reset(r.data);
      if (r.data.logo_url) setLogoPreview(`${API_BASE}${r.data.logo_url}`);
    }).catch(() => {});
    api.get('/empresas/stats').then(r => setStats(r.data)).catch(() => {});
  }, [reset]);

  // ─── Consulta CNPJ na BrasilAPI (Receita Federal) ───────────
  const consultarCNPJ = useCallback(async () => {
    const cnpj = apenasNumeros(cnpjValor);
    if (cnpj.length !== 14) {
      toast.error('Digite um CNPJ válido com 14 dígitos.');
      return;
    }
    setBuscandoCNPJ(true);
    setCnpjStatus(null);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (!res.ok) throw new Error('CNPJ não encontrado na Receita Federal.');
      const d = await res.json();

      // Preenche os campos automaticamente
      setValue('razao_social',       d.razao_social || '');
      setValue('nome_fantasia',      d.nome_fantasia || d.razao_social || '');
      setValue('setor',              d.cnae_fiscal_descricao || '');
      setValue('porte',              mapearPorte(d.descricao_porte || ''));
      setValue('cidade',             mapearCidade(d.municipio || ''));
      setValue('telefone_comercial', d.ddd_telefone_1 ? d.ddd_telefone_1.replace(/(\d{2})(\d+)/, '($1) $2') : '');
      setValue('email_comercial',    d.email ? d.email.toLowerCase() : '');

      // Monta endereço
      const endereco = [
        d.descricao_tipo_logradouro,
        d.logradouro,
        d.numero ? `nº ${d.numero}` : '',
        d.complemento || '',
        d.bairro || '',
      ].filter(Boolean).join(', ');
      if (endereco) setValue('endereco', endereco);

      setCnpjStatus('ok');
      toast.success(`✅ Dados de "${d.razao_social}" preenchidos automaticamente!`);
    } catch (err) {
      setCnpjStatus('erro');
      toast.error(err.message || 'Erro ao consultar CNPJ. Verifique e tente novamente.');
    }
    setBuscandoCNPJ(false);
  }, [cnpjValor, setValue]);

  // ─── Auto-consulta quando CNPJ atinge 14 dígitos ────────────
  useEffect(() => {
    const limpo = apenasNumeros(cnpjValor || '');
    if (limpo.length === 14) {
      consultarCNPJ();
    } else {
      setCnpjStatus(null);
    }
  }, [cnpjValor, consultarCNPJ]);

  const onSubmit = async (d) => {
    setSaving(true);
    try {
      await api.put('/empresas/perfil', d);
      toast.success('Perfil atualizado!');
    } catch { toast.error('Erro ao salvar.'); }
    setSaving(false);
  };

  const handleLogoChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Logo deve ter no máximo 5MB.'); return; }
    setLogo(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const uploadLogo = async () => {
    if (!logo) return;
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('logo', logo);
      await api.post('/empresas/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logomarca atualizada!');
      setLogo(null);
    } catch { toast.error('Erro ao enviar logo.'); }
    setUploadingLogo(false);
  };

  return (
    <Layout title="Perfil da Empresa" subtitle="Gerencie os dados e logomarca da sua empresa">

      {/* Stats rápidos */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            {n:stats.total_matches, l:'Candidatos com Match', c:'text-acre-green'},
            {n:stats.em_selecao,    l:'Em Seleção',           c:'text-blue-600'},
            {n:stats.selecionados,  l:'Aprovados',            c:'text-green-600'},
            {n:stats.recusados,     l:'Recusados',            c:'text-red-500'},
          ].map((s,i)=>(
            <div key={i} className="card p-3 text-center">
              <p className={`font-sora text-2xl font-bold ${s.c}`}>{s.n||0}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      {/* LOGOMARCA */}
      <div className="card p-5 mb-5">
        <h2 className="section-title mb-4">🖼️ Logomarca da Empresa</h2>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50 flex-shrink-0">
            {logoPreview
              ? <img src={logoPreview} alt="Logo" className="w-full h-full object-contain"/>
              : <span className="text-4xl">🏢</span>}
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-600 mb-2">A logomarca aparece nas vagas e no perfil da empresa.</p>
            <div className="flex gap-2 flex-wrap">
              <label className="btn-outline cursor-pointer text-xs px-4 py-2">
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange}/>
                {logoPreview ? '🔄 Trocar Logo' : '📷 Selecionar Logo'}
              </label>
              {logo && (
                <button onClick={uploadLogo} disabled={uploadingLogo}
                  className="btn-primary text-xs px-4 py-2 disabled:opacity-60">
                  {uploadingLogo ? 'Enviando...' : '✅ Salvar Logo'}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">PNG, JPG · recomendado 200×200px · máx. 5MB</p>
          </div>
        </div>
      </div>

      {/* DADOS DA EMPRESA */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="section-title">📋 Dados da Empresa</h2>
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Search size={11}/>
              <span>Digite o CNPJ e os dados serão preenchidos automaticamente</span>
            </div>
          </div>

          {/* ── CNPJ com consulta ── */}
          <div>
            <label className="label">CNPJ</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  className={`input pr-8 ${
                    cnpjStatus === 'ok'   ? 'border-green-400 focus:border-green-500' :
                    cnpjStatus === 'erro' ? 'border-red-400 focus:border-red-500' : ''
                  }`}
                  placeholder="00.000.000/0001-00"
                  {...register('cnpj')}
                />
                {cnpjStatus === 'ok' && (
                  <CheckCircle size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none" />
                )}
                {cnpjStatus === 'erro' && (
                  <AlertCircle size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-red-500 pointer-events-none" />
                )}
              </div>
              <button
                type="button"
                onClick={consultarCNPJ}
                disabled={buscandoCNPJ}
                className="btn-outline text-xs px-3 py-2 flex items-center gap-1.5 flex-shrink-0 disabled:opacity-60"
                title="Consultar dados na Receita Federal"
              >
                {buscandoCNPJ
                  ? <><Loader2 size={13} className="animate-spin" /> Consultando...</>
                  : <><Search size={13} /> Consultar Receita</>}
              </button>
            </div>
            {cnpjStatus === 'ok' && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle size={11}/> Dados preenchidos com informações da Receita Federal
              </p>
            )}
            {cnpjStatus === 'erro' && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle size={11}/> CNPJ não encontrado. Preencha os dados manualmente.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Razão Social</label>
              <input className="input" {...register('razao_social')}/>
            </div>
            <div>
              <label className="label">Nome Fantasia</label>
              <input className="input" {...register('nome_fantasia')}/>
            </div>
            <div>
              <label className="label">Setor / CNAE</label>
              <input className="input" {...register('setor')}/>
            </div>
            <div>
              <label className="label">Porte</label>
              <select className="input" {...register('porte')}>
                <option value="">Selecione...</option>
                {['mei','micro','pequena','media','grande'].map(p=>(
                  <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Cidade</label>
              <select className="input" {...register('cidade')}>
                <option value="">Selecione...</option>
                {MUNICIPIOS.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Telefone Comercial</label>
              <input className="input" {...register('telefone_comercial')}/>
            </div>
            <div>
              <label className="label">E-mail Comercial</label>
              <input className="input" type="email" {...register('email_comercial')}/>
            </div>
            <div>
              <label className="label">Website</label>
              <input className="input" {...register('website')}/>
            </div>
            <div className="col-span-2">
              <label className="label">Endereço</label>
              <input className="input" placeholder="Rua, número, bairro" {...register('endereco')}/>
            </div>
            <div className="col-span-2">
              <DescricaoIA register={register} watch={watch} setValue={setValue} />
            </div>
          </div>
        </div>

        <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60 w-full justify-center py-3 flex items-center gap-2">
          {saving ? <><Loader2 size={15} className="animate-spin"/>Salvando...</> : '💾 Salvar Perfil'}
        </button>
      </form>

      {/* ── Alterar Senha ── */}
      <div className="mt-6 max-w-md">
        <AlterarSenha />
      </div>
    </Layout>
  );
}
